import { apiClient } from './client';
import type { Clinic, PaginatedResponse } from '@/types/models';

// Re-export types for convenience
export type { Clinic };

export interface CreateClinicData {
  name: string;
  address?: string;
  phone?: string;
  cnpj?: string;
  isActive?: boolean;
}

export interface UpdateClinicData extends Partial<CreateClinicData> {}

export interface ClinicsQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const clinicsService = {
  // Get all clinics with pagination
  async getAll(params?: ClinicsQueryParams): Promise<PaginatedResponse<Clinic>> {
    const response = await apiClient.get('/clinics', { params });
    return response.data;
  },

  // Get accessible clinics for current user
  async getAccessible(): Promise<Clinic[]> {
    const response = await apiClient.get('/clinics/accessible');
    return response.data;
  },

  // Get single clinic by ID
  async getById(id: string): Promise<Clinic> {
    const response = await apiClient.get(`/clinics/${id}`);
    return response.data;
  },

  // Create new clinic
  async create(data: CreateClinicData): Promise<Clinic> {
    const response = await apiClient.post('/clinics', data);
    return response.data;
  },

  // Update clinic
  async update(id: string, data: UpdateClinicData): Promise<Clinic> {
    const response = await apiClient.put(`/clinics/${id}`, data);
    return response.data;
  },

  // Toggle clinic status
  async toggleStatus(id: string): Promise<Clinic> {
    const response = await apiClient.patch(`/clinics/${id}/status`);
    return response.data;
  },

  // Delete clinic
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/clinics/${id}`);
  },

  // Add user to clinic
  async addUser(clinicId: string, userId: string): Promise<void> {
    await apiClient.post(`/clinics/${clinicId}/users/${userId}`);
  },

  // Remove user from clinic
  async removeUser(clinicId: string, userId: string): Promise<void> {
    await apiClient.delete(`/clinics/${clinicId}/users/${userId}`);
  },
};